import { SegmentType } from './interfaces/segment.interface';
import { ReportEntityTypes } from './modules/reports/interfaces/header.interface';
import { PeriodValues } from './modules/reports/shared/constants/filterAndSortingKeys';
import { Uuid } from './services/uuid';

const answerQuestions = '/questions';
const companyAttrs = '/company/attributes';
const companyReportsProfile = '/reports/company/profile';
const contentScales = '/content/scales';
const analysis = '/analysis';
const contentSurveys = '/content/surveys';
const contentTopics = '/content/topics';
const myRequestsSuffix = '/list/my-requests';
const okrs = '/okrs';
const okrsObjectivesStream = `${okrs}/key-result-stream`;

const questionProfile = function (questionId: Uuid): string {
  return '/questions/detail/:id'.replace(':id', questionId.toString());
};

const topicsProfile = function (topicId: Uuid): string {
  return '/topics/detail/:id'.replace(':id', topicId.toString());
};
const groupReportsProfile = function (groupId: Uuid): string {
  return '/reports/group/profile/:id'.replace(':id', groupId.toString());
};
const surveyReportsProfile = function (surveyId: Uuid): string {
  return '/reports/survey/profile/:id'.replace(':id', surveyId.toString());
};

const userReportsProfile = function (userId: Uuid): string {
  return '/reports/user/profile/:id'.replace(':id', userId.toString());
};

const userFeedbackMeeting = (userId: string, periodId: string) =>
  `${userReportsProfile(Uuid.fromHexString(userId))}/feedback-meeting/${periodId}`;

const getReportProfileLink = (reportType: ReportEntityTypes | SegmentType, subjectId?: string): string => {
  switch (reportType) {
    case ReportEntityTypes.Company || SegmentType.Company:
      return companyReportsProfile;
    case ReportEntityTypes.User || SegmentType.User:
      return userReportsProfile(Uuid.fromHexString(subjectId || ''));
    case ReportEntityTypes.Survey:
      return surveyReportsProfile(Uuid.fromHexString(subjectId || ''));
    case ReportEntityTypes.Group || SegmentType.Group:
      return groupReportsProfile(Uuid.fromHexString(subjectId || ''));
    default:
      throw Error('there is no such type of report');
  }
};

const createTopicQuestionProfileLink = (reportType: ReportEntityTypes) => {
  return (topicId: Uuid, questionId: Uuid, subjectId?: string) => {
    const uriBits: string[] = [
      getReportProfileLink(reportType, subjectId),
      topicsProfile(topicId),
      questionProfile(questionId),
    ];

    return uriBits.join('/').replace(/[\\/]+/g, '/');
  };
};

const createQuestionsAnswersProfileLink = (
  reportType: SegmentType,
  subjectId: string,
  topicId: Uuid,
  questionId?: Uuid,
  filterAnswerRange?: PeriodValues,
  filterSurvey?: string | null,
) => {
  const query = `filterAnsweredRange=${filterAnswerRange || ''}&filterSurvey=${filterSurvey || ''}`;
  return (
    [
      getReportProfileLink(reportType, subjectId),
      topicsProfile(topicId),
      questionId ? `${questionProfile(questionId)}/answers` : `/list/questions`,
    ]
      .filter(function isNonEmptyString(bit): boolean {
        return Boolean(bit);
      })
      .join('/')
      .replace(/[\\/]+/g, '/') +
    '?' +
    query
  );
};

const getUserProfileMyRequestsLink = (userId: string) => `${userReportsProfile(Uuid.fromHexString(userId))}/${userId}${myRequestsSuffix}`;

export const isFullPageRoute = () => window.location.pathname.includes('/users/signup');

export const routes = {
  reports: {
    company: companyReportsProfile,
    sessionUser: '/user/overview',
    user: userReportsProfile,
    survey: surveyReportsProfile,
    group: groupReportsProfile,
    createTopicQuestionProfileLink: createTopicQuestionProfileLink,
    createQuestionsAnswersProfileLink: createQuestionsAnswersProfileLink,
    getUserProfileMyRequestsLink,
    userFeedbackMeeting: userFeedbackMeeting,
  },
  sessionUser: {
    settings: '/user/settings',
    home: '/user/home',
  },
  company: {
    settings: '/company/settings',
    users: '/company/users',
    importUsers: '/company/settings/import',
    importSummary(id: string): string {
      return `${this.importUsers}/${id}/summary`;
    },
    attributes: companyAttrs,
    attributesAdd: `${companyAttrs}/add`,
  },
  answerQuestions: {
    questions: answerQuestions,
    cycle: `${answerQuestions}/cycle`,
    noSurvey: `${answerQuestions}/no-survey`,
  },
  analysis: {
    heatmap: `${analysis}/heatmap`,
    spider: `${analysis}/spider-web`,
    ai: `${analysis}/ai`,
  },
  contentSurveys: {
    surveys: contentSurveys,
    surveysAdd: `${contentSurveys}/add`,
  },
  contentTopics: {
    topics: contentTopics,
    topicsAdd: `${contentTopics}/add`,
    topic: (id: string) => `${contentTopics}/${id}`,
  },
  contentScales: {
    scales: contentScales,
    scalesAdd: `${contentScales}/add`,
    performanceManagement: '/content/performance-management',
    adminFeedbackCycle: (id: string) => `/admin-feedback-cycle/${id}`
  },
  okrs: {
    list: okrs,
    add: `${okrs}/add`,
    objectiveDetails(id: string): string {
      return `${okrs}/${id}`;
    },
    objectivesStream: {
      updateObjectiveKRs: `${okrsObjectivesStream}`,
      noObjective: `${okrsObjectivesStream}/no-objective`,
      finished: `${okrsObjectivesStream}/finished`,
    },
  },
  selfSignup: (tenantUUID: string, surveyId?: string): string => {
    return `/users/signup/${tenantUUID}${surveyId ? `?survey=${surveyId}` : ''}`;
  },
  contact: '/contact',
  accountSuspended: '/account_suspended',
  login: '/login',
  logout: '/logout',
  forgot_password: '/forgot_password',
  otp: '/otp',
};
